import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { createAuthClient } from '../client/auth-client.js';
import { browserRefreshCredentialStore, } from '../client/refresh-credential-store.js';
import { clearSessionRenewer, createSessionRenewer, registerSessionRenewer, sessionRenewal, } from '../client/session-renewal.js';
import { AuthApiError } from '../errors.js';
import { ACCOUNT_SUSPENDED_ERROR_CODE, onSessionEndRequested, } from './session-end.js';
import { onSessionRecheckRequested } from './session-recheck.js';
/** localStorage key used to persist the bearer token across reloads. */
export const TOKEN_STORAGE_KEY = 'auth.sessionToken';
const AuthContext = React.createContext(null);
function defaultStorage() {
    if (typeof window === 'undefined')
        return null;
    try {
        return window.localStorage;
    }
    catch {
        return null;
    }
}
/**
 * Top-level provider that owns the bearer token and the typed API client.
 *
 * Wrap the app once near the root: `<AuthProvider apiBase={env.apiUrl} />`.
 * All other hooks in this package read their dependencies from this context.
 */
export function AuthProvider({ children, apiBase, tokenStorage, onUnauthenticated, }) {
    const storage = React.useMemo(() => tokenStorage ?? defaultStorage(), [tokenStorage]);
    // Initial token is hydrated synchronously so the first render has the right
    // shape and we don't flicker an "unauthenticated" UI on reload.
    const [token, setTokenState] = React.useState(() => storage?.getItem(TOKEN_STORAGE_KEY) ?? null);
    const [user, setUser] = React.useState(null);
    const [sessionEndedBecause, setSessionEndedBecause] = React.useState(null);
    // tokenRef is the source of truth the API client reads via `getToken()`. We can NOT defer
    // its update to a useEffect — child components' effects (e.g. `useAuthMe.load()` inside the
    // app's <UnauthenticatedRedirector>) fire BEFORE the parent's effects, so a deferred sync
    // would mean the post-login `/auth/me` call goes out with the OLD token still in the ref →
    // 401 → setUser(null) → bounce back to /login. The user then logs in a second time
    // (because the ref is now warm) and lands on the dashboard. We saw this as "login takes
    // two attempts."
    // Update tokenRef synchronously inside setToken instead. The ref now matches whatever was
    // last written, regardless of when React commits the state update.
    const tokenRef = React.useRef(token);
    const onUnauthenticatedRef = React.useRef(onUnauthenticated);
    React.useEffect(() => {
        onUnauthenticatedRef.current = onUnauthenticated;
    }, [onUnauthenticated]);
    const setToken = React.useCallback((next) => {
        tokenRef.current = next;
        setTokenState(next);
        // A new credential is a new session, and the last session's ending is no longer about it.
        if (next !== null && next.length > 0)
            setSessionEndedBecause(null);
        if (storage === null)
            return;
        if (next === null || next.length === 0) {
            storage.removeItem(TOKEN_STORAGE_KEY);
        }
        else {
            storage.setItem(TOKEN_STORAGE_KEY, next);
        }
    }, [storage]);
    // Where the 30-day credential lives. One seam, so the day the API gets a hostname under
    // `h2hspark.com` this is the only thing that changes — see `refresh-credential-store.ts`.
    const refreshCredentials = React.useMemo(() => browserRefreshCredentialStore(storage), [storage]);
    // The client is created exactly once. Token reads always go through the ref
    // so the same client instance picks up newer tokens without being recreated
    // (which would invalidate every memoised hook in consumer apps).
    const client = React.useMemo(() => createAuthClient({
        apiBase,
        getToken: () => tokenRef.current,
        setToken,
        refreshCredentials,
        credentialFitToSend: sessionRenewal.credentialFitToSend,
        onUnauthenticated: () => onUnauthenticatedRef.current?.(),
    }), [apiBase, setToken, refreshCredentials]);
    // **The renewer, installed during render rather than from an effect.**
    //
    // React runs a child's effects before its parent's. `useAuthMe` asks `/auth/me` from a mount
    // effect, so a renewer installed in *this* component's effect would not exist yet when the
    // first request of the session goes out — and that request is the one that matters most: a
    // member opening the app the next morning, holding an expired access token and a refresh
    // credential with twenty-nine days left. Registering in a `useMemo` runs before any child
    // renders, let alone mounts. `session-recheck.ts` hit the same ordering and latched the
    // request; latching is wrong here, because a page with no provider would then block forever.
    //
    // The renewal itself is single-flight and lives in `session-renewal.ts`; this is only the part
    // that knows *how* to renew, which is the part that needs the client and the credentials.
    const renewer = React.useMemo(() => {
        const renew = createSessionRenewer({ client, setToken, refreshCredentials });
        registerSessionRenewer(renew);
        return renew;
    }, [client, setToken, refreshCredentials]);
    React.useEffect(() => () => clearSessionRenewer(renewer), [renewer]);
    // If the token disappears (logout, manual storage clear, expired refresh),
    // make sure the cached user follows it so guards re-render correctly.
    React.useEffect(() => {
        if (token === null)
            setUser(null);
    }, [token]);
    /**
     * **The session ends here, for a reason the sign-in screen can show** (`PLT-014` AC-7).
     *
     * The reason is recorded *first* so that the render in which `user` becomes null already
     * carries it — the app's redirector reads both in one effect, and a reason that arrived a
     * render later would send the browser to sign-in with nothing to say.
     *
     * Both credentials go. Leaving the refresh credential would let the next request renew a
     * session the server has just refused by name, which is the same argument the re-check's
     * failure branch makes below.
     */
    const endSession = React.useCallback((reason) => {
        setSessionEndedBecause(reason);
        setToken(null);
        refreshCredentials.clear();
        setUser(null);
    }, [setToken, refreshCredentials]);
    // **A refusal that names the account ends the session without asking.** `reportRefusal` in
    // `session-end.ts` is what the transport calls; it sends every other refusal to the re-check
    // below and this one here, because `/auth/me` would answer "alive" about a valid token whose
    // account is suspended, and the console would keep a session the server has refused by name.
    React.useEffect(() => onSessionEndRequested(endSession), [endSession]);
    // **A domain call's 401 asks the session to prove itself.** `createApiRequest` fires
    // `requestSessionRecheck` when a request that carried a token comes back 401; this re-runs
    // `/auth/me`, which is the authority. A session that has really ended answers 401 here too,
    // `setUser(null)` follows, and the app's redirector sends the browser to sign-in. A 401 that
    // was only about one resource leaves the session alone.
    //
    // Without it an expired token stranded the console on its loading skeletons until somebody
    // refreshed the browser: the domain calls 401'd, the cached user stayed non-null, and nothing
    // re-checked. See `session-recheck.ts`.
    React.useEffect(() => onSessionRecheckRequested(() => {
        if (tokenRef.current === null)
            return;
        void client
            .getMe()
            .then((next) => {
            // A session ended while this question was in flight — a suspension reported by
            // another request, a sign-out in this tab — must not be restored by the answer to
            // a question asked before it ended.
            if (tokenRef.current === null)
                return;
            setUser(next);
        })
            .catch((error) => {
            // The authority itself can say the account is suspended: `/auth/me` runs through
            // the same session gate every route does. Then the session ends *with its reason*,
            // so the sign-in screen can say why, rather than as an anonymous failure.
            if (error instanceof AuthApiError && error.code === ACCOUNT_SUSPENDED_ERROR_CODE) {
                endSession('account-suspended');
                return;
            }
            // Any failure here — 401 or otherwise — is answered by clearing the token, which is
            // the same end state a refresh reached. `getMe` throwing while a token is present is
            // not a session anything should keep acting on.
            //
            // The refresh credential goes too. `/auth/me` is renewal-gated, so reaching this
            // branch means renewal had its chance and the session is over; leaving a 30-day
            // credential in storage after that would let the next request quietly renew a session
            // the authority has just refused.
            setToken(null);
            refreshCredentials.clear();
            setUser(null);
        });
    }), [client, setToken, refreshCredentials, endSession]);
    // Keep multiple tabs in sync: when one tab logs in/out, the other reflects it.
    React.useEffect(() => {
        if (typeof window === 'undefined')
            return undefined;
        const handler = (event) => {
            if (event.key !== TOKEN_STORAGE_KEY)
                return;
            // localStorage.removeItem dispatches a StorageEvent with newValue === null (NOT
            // an empty string) — the previous `=== ''` check meant cross-tab logout never
            // propagated. Normalize empty strings too as belt-and-suspenders.
            const raw = event.newValue;
            const next = raw === null || raw === '' ? null : raw;
            tokenRef.current = next;
            setTokenState(next);
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);
    const value = React.useMemo(() => ({ client, token, setToken, user, setUser, endSession, sessionEndedBecause }), [client, token, setToken, user, endSession, sessionEndedBecause]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
/** Returns the live context value. Throws if used outside `<AuthProvider>`. */
export function useAuthContext() {
    const value = React.useContext(AuthContext);
    if (value === null) {
        throw new Error('Auth hooks must be used inside an <AuthProvider>.');
    }
    return value;
}
/** Returns the typed API client. */
export function useAuthClient() {
    return useAuthContext().client;
}
//# sourceMappingURL=auth-provider.js.map